import { PERMANENT_COOKIE_NAME } from '@packages/domain/constants/cookies';
import { SESSION_ID_NAME } from '@packages/domain/constants/storage';
import { session as sessionStorage } from '@packages/domain/utils/storage/session';
import { windowUtils } from '@packages/domain/utils/windowUtils/windowUtils';
import { HEAP_ENVS } from 'constants/config';
import type { Store } from 'redux';
import { getPaymentId } from 'selectors/entities/payment/payment';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getCountryCode } from 'selectors/geolocation/geolocation';
import { getCurrentLocale, getPlatform } from 'selectors/ui/ui';
import { store as defaultStore } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';

type WindowWithHeap = typeof window & { heap: Heap | null };

// biome-ignore lint/suspicious/noExplicitAny: false positive
let configuredStore: any = null;

interface Heap {
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (identity: string) => void;
  resetIdentity: () => void;
  addUserProperties: (properties: Record<string, unknown>) => void;
  addEventProperties: (properties: Record<string, unknown>) => void;
  removeEventProperty: (property: string) => void;
  clearEventProperties: () => void;
  appid: string;
  userId: string;
  identity: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: false positive
  config: any;
}

const heapAgent = {
  configure: function (store: Store) {
    configuredStore = store;
  },

  initialize: () => {
    try {
      const store = configuredStore || defaultStore;
      const state = store.get()?.getState() ?? {};
      const isEnabled = HEAP_ENVS.includes(windowUtils.getHostname());

      if (!isEnabled) {
        return;
      }

      const heap = (window as WindowWithHeap).heap;

      if (typeof heap !== 'undefined' && heap) {
        const attrs = {
          peer_tracker_id: cookies.read(PERMANENT_COOKIE_NAME),
          session_id: sessionStorage.getItem(SESSION_ID_NAME),
          source_url: windowUtils.getHref(),
          browser_language: getCurrentLocale(state),
          country: getCountryCode(state),
          portal_code: getRecipientId(state),
          user_agent: navigator.userAgent,
          referrer: document.referrer,
          platform: getPlatform(state),
          paymentid: getPaymentId(state) || null,
        };

        attrs.peer_tracker_id && heap.identify(attrs.peer_tracker_id);
        heap.addUserProperties(attrs);
      }
    } catch (error) {
      console.debug(error);
    }
  },

  trackEvent: (eventName: string, metadata: Record<string, unknown>) => {
    try {
      const store = configuredStore || defaultStore;
      const state = store.get()?.getState() ?? {};

      const heap = (window as WindowWithHeap).heap;

      if (typeof heap !== 'undefined' && heap) {
        const eventBody = {
          id: cookies.read(PERMANENT_COOKIE_NAME),
          account_id: getRecipientId(state),
          ...metadata,
        };

        heap.track(eventName, eventBody);
      } else {
        console.debug('Cannot track event, heap not found');
      }
    } catch (error) {
      console.debug(error);
    }
  },
};

export { heapAgent };
